import {
  IconCircle,
  TitleWrapper,
  NextButton,
} from "../../pages/DevicePage/DevicePage.styles";
import {
  ArrowNavigationRightOutlined,
  MenuMoreOutlined,
} from "@gds-react/icons";
import {
  CapabilitiesBase,
  DevicePropertiesBaseCommunicationDeviceProperties,
  FqdaBaseGeniFqda,
} from "../../generated-api/device";
import CopyWrapper from "../../components/CopyWrapper";
import { GetIconByDeviceType } from "../helpers/getIconByDeviceType";
import { useState } from "react";
import { Button, Icon } from "@gds-react/core";
import { getCallbackUrl } from "../helpers/getCallbackUrl";
import CoreDeviceConnectionState from "@gf-core/widgets/widgets/CoreDeviceConnectionState";
import {
  CoreDeviceFieldUpdate,
  CoreDeviceFieldUpdateSize,
} from "@gf-core/widgets/components/CoreDeviceFieldUpdate";
import CoreDeviceList from "@gf-core/widgets/widgets/CoreDeviceList";
import { CoreDeviceListColumnDef } from "@gf-core/widgets/types/CoreDeviceListColumnDef";
import CoreNetworkQuality from "@gf-core/widgets/widgets/CoreNetworkQuality";
import CoreLanConnection from "@gf-core/widgets/widgets/CoreLanConnection";
import CoreFirmwareUpdate from "@gf-core/widgets/widgets/CoreFirmwareUpdate";
import CoreDeviceContextMenu from "@gf-core/widgets/widgets/CoreDeviceContextMenu";
import { CoreContextMenuActionsEnum } from "@gf-core/widgets/types/CoreContextMenuActionsEnum";
import {
  SubscriptionStatusWidget,
  SubscriptionStatus,
} from "@gf-ww-service-portal/widgets";

export const defaultColumns = (
  isInstallationPage: boolean,
  installationId: string,
  onUpdate: () => void,
  onRedirectToAppStore: (productId: string) => void,
  withFirmwareButton = false,
  withNetworkQuality = false,
  withDeviceState = false,
  t: (key: string) => string,
  accountId?: string,
) => {
  const columns = [
    {
      id: "deviceTypeName",
      header: t("deviceList.name"),
      accessorKey: "name",
      cell: ({ row }) => {
        return (
          <TitleWrapper>
            <IconCircle>
              {GetIconByDeviceType(
                row.original.$deviceType,
                row.original.displayFamilyName,
                18,
                "white",
              )}
            </IconCircle>
            <CoreDeviceFieldUpdate
              installationId={installationId}
              deviceTwinId={row.original.deviceTwinId}
              size={CoreDeviceFieldUpdateSize.base}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {row.original.name}
            </CoreDeviceFieldUpdate>
          </TitleWrapper>
        );
      },
      size: 100,
      minSize: 160,
      meta: {
        mobileHeaderTitle: t("deviceList.name"),
      },
    },
    {
      id: "productName",
      header: t("deviceList.productName"),
      accessorKey: "displayName",
      cell: (cell) => cell.getValue(),
      size: 60,
      minSize: 100,
      meta: {
        mobileHeaderTitle: t("deviceList.productName"),
      },
    },
  ] as CoreDeviceListColumnDef[];

  if (withDeviceState) {
    columns.push({
      id: "state",
      header: "State",
      cell: (cell) => {
        return cell.row.original.capabilities ? (
          <CoreDeviceList.Badge>
            {(cell.row.original.capabilities as CapabilitiesBase | undefined)
              ?.enabled
              ? "Enabled"
              : "Disabled"}
          </CoreDeviceList.Badge>
        ) : null;
      },
      size: 20,
      minSize: 20,
      meta: {
        mobileHeaderTitle: "State",
      },
    });
  }

  columns.push({
    id: "familyName",
    header: t("deviceList.category"),
    accessorKey: "displayFamilyName",
    cell: (cell) => cell.getValue(),
    size: 60,
    minSize: 80,
    meta: {
      mobileHeaderTitle: t("deviceList.category"),
    },
  });

  columns.push({
    id: "deviceTwinId",
    accessorKey: "deviceTwinId",
    header: t("deviceList.deviceId"),
    cell: (cell) => {
      return (
        <CopyWrapper direction="rtl">{cell.getValue() as string}</CopyWrapper>
      );
    },
    size: 140,
    minSize: 200,
    meta: {
      mobileHeaderTitle: t("deviceList.deviceId"),
    },
  });

  if (!withNetworkQuality) {
    columns.push({
      id: "channelType",
      header: t("deviceList.channelType"),
      accessorKey: "fqda",
      cell: (cell) => {
        const properties = cell.row.original.fqda as
          | FqdaBaseGeniFqda
          | undefined;

        return properties?.channelType;
      },
      size: 60,
      maxSize: 140,
      meta: {
        mobileHeaderTitle: t("deviceList.channelType"),
      },
    });

    columns.push({
      id: "address",
      header: t("deviceList.geniAddress"),
      accessorKey: "fqda",
      cell: (cell) => {
        const properties = cell.row.original.fqda as
          | FqdaBaseGeniFqda
          | undefined;

        return properties?.address;
      },
      size: 60,
      maxSize: 140,
      meta: {
        mobileHeaderTitle: t("deviceList.geniAddress"),
      },
    });
  }

  if (withNetworkQuality) {
    columns.push({
      id: "NetworkQuality",
      header: t("deviceList.networkQuality"),
      cell: ({ row }) => {
        const properties = row.original.properties as
          | DevicePropertiesBaseCommunicationDeviceProperties
          | undefined;
        if (properties?.addressType === "Imei") {
          return (
            <div style={{ margin: "0 20px", minWidth: 70, maxWidth: 70 }}>
              <CoreNetworkQuality
                installationId={installationId}
                deviceTwinId={row.original.deviceTwinId}
                as="icon"
              />
            </div>
          );
        } else if (properties?.addressType === "Mac") {
          return (
            <div style={{ margin: "0 22px", minWidth: 40, maxWidth: 70 }}>
              <CoreLanConnection
                installationId={installationId}
                deviceTwinId={row.original.deviceTwinId}
                as="icon"
              />
            </div>
          );
        } else {
          return null;
        }
      },
      size: 140,
      maxSize: 140,
      minSize: 140,
      meta: {
        mobileHeaderTitle: t("deviceList.networkQuality"),
        disableClick: true,
      },
    });
  }

  if (withFirmwareButton) {
    columns.push({
      id: "FirmwareUpdate",
      header: t("deviceList.firmwareUpdate"),
      cell: ({ row }) => {
        return (
          row.original.$deviceType === "GdpCommunicationDevice" && (
            <CoreFirmwareUpdate
              installationId={installationId}
              deviceTwinId={row.original.deviceTwinId}
            />
          )
        );
      },
      size: 120,
      minSize: 160,
      maxSize: 220,
      meta: {
        mobileHeaderTitle: t("deviceList.firmwareUpdate"),
        disableClick: true,
      },
    });
  }

  if (withFirmwareButton) {
    columns.push({
      id: "ConnectionState",
      header: t("deviceList.connection"),
      cell: ({ row }) => {
        return (
          (row.original.$deviceType === "GdpCommunicationDevice" ||
            row.original.$deviceType === "MoxaDevice") && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <CoreDeviceConnectionState
                device={row.original}
                as="icon"
                withLabel
              />
            </div>
          )
        );
      },
      size: 100,
      minSize: 120,
      maxSize: 140,
      meta: {
        mobileHeaderTitle: t("deviceList.connection"),
      },
    });
  }

  if (isInstallationPage) {
    columns.push({
      id: "subscription",
      header: "Subscription",
      cell: ({ row }) => {
        return (
          <SubscriptionStatusWidget
            accountId={accountId ?? ""}
            deviceTwinId={row.original.deviceTwinId}
          >
            <SubscriptionStatus showTooltip showSubscriptionDetails />
          </SubscriptionStatusWidget>
        );
      },
      size: 60,
      minSize: 80,
      meta: {
        mobileHeaderTitle: t("deviceList.category"),
      },
    });
  }

  columns.push(
    {
      id: "contextMenu",
      header: t("deviceList.actions"),
      cell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [showMenu, setShowMenu] = useState(false);

        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CoreDeviceContextMenu
              accountId={accountId}
              onRedirectToAppStore={
                accountId ? onRedirectToAppStore : undefined
              }
              installationId={installationId}
              deviceTwinId={row.original.deviceTwinId}
              device={row.original}
              show={showMenu}
              popoverPosition={["bottom", "top"]}
              wibutlerCallbackUrl={getCallbackUrl()}
              onWidgetClose={() => {
                onUpdate();
              }}
              onClose={() => {
                setShowMenu(false);
              }}
              activeMenuActions={[
                CoreContextMenuActionsEnum.CHANGE_CONFIGURATION,
                CoreContextMenuActionsEnum.DELETE_DEVICE,
                CoreContextMenuActionsEnum.MODBUS_SETTINGS,
                CoreContextMenuActionsEnum.REDISCOVER,
                CoreContextMenuActionsEnum.REPLACE_GATEWAY,
                CoreContextMenuActionsEnum.UPDATE_DEVICE,
                CoreContextMenuActionsEnum.SHOW_DEVICE_DETAILS,
                CoreContextMenuActionsEnum.SHOW_DEVICE_HIERARCHY,
                CoreContextMenuActionsEnum.SHOW_DEVICE_RULE,
                CoreContextMenuActionsEnum.SHOW_REPROFILE,
                CoreContextMenuActionsEnum.RESET_SOFTWARE,
              ]}
            >
              <Button
                size="small"
                kind="tertiary-subtle"
                onClick={(e) => {
                  setShowMenu(true);
                  e.stopPropagation();
                }}
              >
                <Icon src={<MenuMoreOutlined />} />
              </Button>
            </CoreDeviceContextMenu>
          </div>
        );
      },
      size: 90,
      maxSize: 90,
      minSize: 90,
      meta: {
        mobileHeaderTitle: t("deviceList.actions"),
        disableClick: true,
      },
    },
    {
      id: "actions",
      accessorKey: "type",
      header: "",
      cell: () => (
        <NextButton>
          <ArrowNavigationRightOutlined style={{ width: 20 }} />
        </NextButton>
      ),
      size: 24,
      maxSize: 24,
      minSize: 56,
      meta: {
        mobileHeaderTitle: "",
      },
    },
  );

  return columns;
};
